import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { peopleGateway } from '../index';
import { AddEmployeeDialog } from '../components/AddEmployeeDialog';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { AllEmployeesList } from '../components/AllEmployeesList';
import { emptyContractorDraft, toCreateContractorInput } from '../contractorForm';
import { contractorIdentificationTypeLabels, type ContractorResponse } from '../types';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

function maskLast4(last4: string | null): string {
  return last4 ? `•••-••-${last4}` : '—';
}

/** EPN-45 — the URL (`/people/employees` or `/people/contractors`, see App.tsx) is the single source of
 * truth for which tab is showing, not internal component state, so a refresh or a direct link from the
 * sidebar always lands on the right population — matching that requirement for the two distinct side menu
 * leaves that reuse this page (People > Employees > "All Employees" and People > Contractors > "Contractor
 * List"). An unrecognized/missing `section` param defaults to employees rather than erroring.
 *
 * EPN-203: the Employees tab is the approved All Employees list (`AllEmployeesList`); the Contractors tab keeps
 * its existing list. */
export function PeopleListPage() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [listKey, setListKey] = useState(0);
  const tab: 'employees' | 'contractors' = section === 'contractors' ? 'contractors' : 'employees';
  const setTab = (next: 'employees' | 'contractors') => navigate(`/people/${next}`);
  const [employeeTotalCount, setEmployeeTotalCount] = useState<number | null>(null);
  const [contractors, setContractors] = useState<ContractorResponse[]>([]);
  const [contractorTotalCount, setContractorTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);

  const [contractorName, setContractorName] = useState('');
  const [contractorEmail, setContractorEmail] = useState('');

  // Debounce the search box so every keystroke doesn't fire a request — the query itself still runs
  // server-side, this just paces how often it runs.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Tab counts: unfiltered totals from the server (one row requested, only the count is used). A failure just
  // leaves the count off the tab label.
  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      peopleGateway.listEmployees({ page: 1, pageSize: 1 }),
      peopleGateway.listContractors({ page: 1, pageSize: 1 }),
    ]).then(([emp, con]) => {
      if (cancelled) return;
      if (emp.status === 'fulfilled') setEmployeeTotalCount(emp.value.totalCount);
      if (con.status === 'fulfilled') setContractorTotalCount(con.value.totalCount);
    });
    return () => { cancelled = true; };
  }, [tab]);

  // The Contractors tab: server-side search and pagination (EPN-74).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const con = await peopleGateway.listContractors({
        search: debouncedSearch || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setContractors(con.items);
      setContractorTotalCount(con.totalCount);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load people.'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (tab !== 'contractors') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load, tab]);

  const totalPages = Math.max(1, Math.ceil((contractorTotalCount ?? 0) / PAGE_SIZE));

  async function handleAddContractor(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await peopleGateway.createContractor(toCreateContractorInput({ ...emptyContractorDraft, displayName: contractorName, email: contractorEmail }));
      setContractorName(''); setContractorEmail('');
      setShowAddForm(false);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not add contractor.'));
    }
  }

  async function handleToggleContractorStatus(con: ContractorResponse) {
    setError(null);
    try {
      await peopleGateway.updateContractor(con.id, {
        displayName: con.displayName, businessName: con.businessName, email: con.email,
        identificationType: con.identificationType, identificationLast4: con.identificationLast4,
        preferredPayMethod: con.preferredPayMethod, w9ReceivedOn: con.w9ReceivedOn, form1099Configured: con.form1099Configured, isActive: !con.isActive,
      });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not change contractor status.'));
    }
  }

  const tabs = (
    <div className="tabs">
      <button type="button" className={tab === 'employees' ? 'tab tab--active' : 'tab'} onClick={() => { setTab('employees'); setPage(1); }}>
        Employees{employeeTotalCount === null ? '' : ` (${employeeTotalCount})`}
      </button>
      <button type="button" className={tab === 'contractors' ? 'tab tab--active' : 'tab'} onClick={() => { setTab('contractors'); setPage(1); }}>
        Contractors{contractorTotalCount === null ? '' : ` (${contractorTotalCount})`}
      </button>
    </div>
  );

  if (tab === 'employees') {
    return (
      <section className="card card--wide">
        {tabs}
        <AllEmployeesList key={listKey} onAddEmployee={() => setShowAddEmployee(true)} />
        {showAddEmployee && <AddEmployeeDialog onClose={() => setShowAddEmployee(false)} onInvited={() => setListKey((k) => k + 1)} />}
      </section>
    );
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>People</h2>
        <button type="button" onClick={() => setShowAddForm((v) => !v)}>
          {showAddForm ? 'Cancel' : 'Add contractor'}
        </button>
      </div>

      {tabs}

      {error && <p className="error">{error}</p>}

      {showAddForm && (
        <form className="inline-form" onSubmit={handleAddContractor}>
          <input placeholder="Display name" value={contractorName} onChange={(e) => setContractorName(e.target.value)} required />
          <input type="email" placeholder="Email" value={contractorEmail} onChange={(e) => setContractorEmail(e.target.value)} required />
          <button type="submit">Create</button>
        </form>
      )}

      <input className="search-box" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />

      {loading && <p className="muted">Loading…</p>}

      {!loading && !error && (
        <table>
          <thead>
            <tr><th>Name</th><th>Email</th><th>ID</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {contractors.map((con) => (
              <tr key={con.id}>
                <td>{con.displayName}</td>
                <td>{con.email}</td>
                <td>{contractorIdentificationTypeLabels[con.identificationType]} {maskLast4(con.identificationLast4)}</td>
                <td>{con.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <button type="button" className="link" onClick={() => navigate(`/people/contractors/${con.id}`)}>Open</button>
                  <button type="button" className="link" onClick={() => void handleToggleContractorStatus(con)}>
                    {con.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
            {contractors.length === 0 && <tr><td colSpan={5} className="muted">No contractors found.</td></tr>}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="muted">Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </section>
  );
}
